// frontend/src/Components/SyncTo/hooks/useSyncToRecorder.js
// Phase 30 — MediaRecorder + AnalyserNode + rAF loop. Owns:
//   - getUserMedia stream (mic)
//   - MediaRecorder instance (5s timeslice chunks)
//   - AudioContext + AnalyserNode (RMS for level meter + silence)
//   - rAF loop (timer tick + level + silence detector)
//   - Sequence counter + 0-byte streak guard
//
// Exposes imperative methods (start/pause/resume/stop/cancel) and reactive
// state (state, timer, level, sessionKey, mimeType) to the provider.
//
// Callbacks (edge events) are caller-provided so the provider can turn them
// into toasts / dialogs via NotificationContext.
//
// PITFALLS #5 (0-byte chunks): 5-in-a-row halt.
// SYNC-04: mic-denied, first-2s silence, rolling 30s warn, 60s auto-stop.
// D-04 / D-05: Stop and auto-stop both trigger download + purge.
// SYNC-06: Cancel prompts confirm, purges IDB, zero network.

import { useCallback, useEffect, useRef, useState } from 'react';
import { pickMimeType } from '../helpers/mimePicker';
import {
  createSilenceDetector,
  FIRST_SILENCE_MS, // imported for contract visibility (constants are the API)
} from '../helpers/silenceDetector';
import { makeSessionKey } from '../helpers/sessionKey';
import { downloadBlob, buildDownloadFilename } from '../helpers/downloadBlob';
import { readUserIdFromJwt } from '../helpers/userIdFromJwt';
import {
  createSession,
  appendChunk,
  assembleBlob,
  purgeBySessionKey,
} from './useSyncToIDB';

const AUDIO_BITS_PER_SECOND = 32000;   // ~14 MB/hr Opus mono
const CHUNK_TIMESLICE_MS    = 5000;    // SYNC-03 5-second chunks
const MAX_ZERO_BYTE_STREAK  = 5;       // PITFALLS #5 — T-30-09

// Silence the unused-import lint without breaking the contract-visibility comment.
void FIRST_SILENCE_MS;

export function useSyncToRecorder(callbacks = {}) {
  const {
    onMicDenied, onZeroByteFailure,
    onFirstSilence, onRollingSilenceWarn, onAutoStop,
    onStopped, onCancelled,
    downloadOnStop = true,  // Phase 32 — RecordingProvider passes false
                            // to suppress Stop-path download. Orphan
                            // Resume/Download path still calls downloadBlob
                            // directly elsewhere — unchanged.
  } = callbacks;

  // ── Refs for non-rendering resources ──
  const streamRef     = useRef(null);
  const recorderRef   = useRef(null);
  const audioCtxRef   = useRef(null);
  const analyserRef   = useRef(null);
  const rafRef        = useRef(null);
  const startedAtRef  = useRef(0);
  const elapsedRef    = useRef(0);
  const seqRef        = useRef(0);
  const sessionKeyRef = useRef(null);
  const mimeTypeRef   = useRef(null);
  const zeroByteRef   = useRef(0);
  const silenceRef    = useRef(null);
  const stateRef      = useRef('idle');

  // ── Reactive state for UI ──
  const [state, setState]             = useState('idle');
  const [timer, setTimer]             = useState(0);
  const [level, setLevel]             = useState(0);
  const [sessionKey, setSessionKey]   = useState(null);
  const [mimeType, setMimeType]       = useState(null);

  const syncState = useCallback((next) => { stateRef.current = next; setState(next); }, []);

  // Forward-declared holder so tickLoop can trigger stop() without TDZ issues.
  const stopRef = useRef(null);

  // ── rAF loop: RMS → timer tick → level → silenceDetector ──
  const tickLoop = useCallback(() => {
    if (stateRef.current === 'idle' || stateRef.current === 'stopping') return;
    const analyser = analyserRef.current;
    if (!analyser) return;

    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sumSq = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buf.length);
    setLevel(rms);

    if (stateRef.current === 'recording') {
      const now = performance.now();
      const newElapsed = elapsedRef.current + (now - startedAtRef.current);
      setTimer(newElapsed);

      const ev = silenceRef.current?.feed(rms, now);
      if (ev?.firstSilenceFired)   onFirstSilence?.();
      if (ev?.rollingWarnFired)    onRollingSilenceWarn?.();
      if (ev?.autoStopFired) {
        stopRef.current?.().then(() => onAutoStop?.()).catch(() => {});
        return;
      }
    }

    rafRef.current = requestAnimationFrame(tickLoop);
  }, [onFirstSilence, onRollingSilenceWarn, onAutoStop]);

  // ── start(meta) ──
  const start = useCallback(async (meta = {}) => {
    if (stateRef.current !== 'idle') return;
    syncState('starting');

    const mime = pickMimeType();
    if (!mime) {
      syncState('idle');
      throw new Error('No supported MIME — SyncToButton D-02 branch should prevent this');
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
    } catch (err) {
      syncState('idle');
      if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        onMicDenied?.(err);
      }
      throw err;
    }
    streamRef.current = stream;

    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    src.connect(analyser);
    audioCtxRef.current = ctx;
    analyserRef.current = analyser;

    const sk = makeSessionKey();
    const userId = readUserIdFromJwt();
    sessionKeyRef.current = sk; setSessionKey(sk);
    mimeTypeRef.current   = mime; setMimeType(mime);
    seqRef.current = 0;
    zeroByteRef.current = 0;
    silenceRef.current = createSilenceDetector();

    await createSession({
      sessionKey: sk,
      userId,
      appointmentId: meta.appointmentId,
      patientLabel:  meta.patientLabel,
      startedAt: Date.now(),
      mimeType: mime,
      lastSeq: -1,
    });

    const recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
    recorderRef.current = recorder;

    recorder.ondataavailable = async (e) => {
      if (!e.data || e.data.size === 0) {
        zeroByteRef.current += 1;
        if (zeroByteRef.current >= MAX_ZERO_BYTE_STREAK) {
          try { recorder.stop(); } catch {}
          onZeroByteFailure?.();
        }
        return;
      }
      zeroByteRef.current = 0;
      try {
        await appendChunk({
          sessionKey: sk,
          userId,
          seq: ++seqRef.current,
          blob: e.data,
          mimeType: mime,
          recordedAt: Date.now(),
        });
      } catch (err) {
        onZeroByteFailure?.();
      }
    };

    elapsedRef.current  = 0;
    startedAtRef.current = performance.now();
    setTimer(0); setLevel(0);

    recorder.start(CHUNK_TIMESLICE_MS);
    syncState('recording');
    rafRef.current = requestAnimationFrame(tickLoop);
  }, [syncState, tickLoop, onMicDenied, onZeroByteFailure]);

  // ── pause / resume ──
  const pause = useCallback(() => {
    if (stateRef.current !== 'recording') return;
    const recorder = recorderRef.current;
    if (!recorder) return;
    elapsedRef.current += performance.now() - startedAtRef.current;
    recorder.pause();
    syncState('paused');
  }, [syncState]);

  const resume = useCallback(() => {
    if (stateRef.current !== 'paused') return;
    const recorder = recorderRef.current;
    if (!recorder) return;
    startedAtRef.current = performance.now();
    recorder.resume();
    syncState('recording');
    rafRef.current = requestAnimationFrame(tickLoop);
  }, [syncState, tickLoop]);

  // ── stop (D-04 download + purge) ──
  const stop = useCallback(async () => {
    if (stateRef.current === 'idle' || stateRef.current === 'stopping') return;
    syncState('stopping');
    const recorder = recorderRef.current;
    const sk = sessionKeyRef.current;
    const mime = mimeTypeRef.current;

    await new Promise((resolve) => {
      if (!recorder || recorder.state === 'inactive') return resolve();
      const onStop = () => { recorder.removeEventListener('stop', onStop); resolve(); };
      recorder.addEventListener('stop', onStop);
      try { recorder.stop(); } catch { resolve(); }
    });

    let blob = null;
    try {
      blob = await assembleBlob(sk, mime);
      if (blob && blob.size > 0) {
        if (downloadOnStop) {
          downloadBlob(blob, buildDownloadFilename(sk, mime));
        }
      }
    } finally {
      // Only purge here when the caller opted into the direct-download flow
      // (downloadOnStop=true). When an onStopped callback is registered, the
      // caller owns the chunks so FailureModal can re-assemble / download.
      if (downloadOnStop) {
        try { await purgeBySessionKey(sk); } catch {}
      }
    }

    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { await audioCtxRef.current?.close(); } catch {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    recorderRef.current = null; streamRef.current = null;
    audioCtxRef.current = null; analyserRef.current = null;
    rafRef.current = null; silenceRef.current = null;
    sessionKeyRef.current = null; mimeTypeRef.current = null;
    elapsedRef.current = 0; startedAtRef.current = 0;
    seqRef.current = 0; zeroByteRef.current = 0;
    setSessionKey(null); setMimeType(null); setTimer(0); setLevel(0);
    syncState('idle');

    onStopped?.(blob);
  }, [syncState, onStopped]);

  // Keep stopRef in sync so tickLoop's stale closure can still call current stop.
  useEffect(() => { stopRef.current = stop; }, [stop]);

  // ── cancel (SYNC-06 — confirm + purge + ZERO network) ──
  const cancel = useCallback(async () => {
    if (stateRef.current === 'idle') return false;
    const ok = window.confirm('Discard this recording? This cannot be undone.');
    if (!ok) return false;

    syncState('stopping');
    const recorder = recorderRef.current;
    const sk = sessionKeyRef.current;

    await new Promise((resolve) => {
      if (!recorder || recorder.state === 'inactive') return resolve();
      const onStop = () => { recorder.removeEventListener('stop', onStop); resolve(); };
      recorder.addEventListener('stop', onStop);
      try { recorder.stop(); } catch { resolve(); }
    });

    try { await purgeBySessionKey(sk); } catch {}

    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { await audioCtxRef.current?.close(); } catch {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    recorderRef.current = null; streamRef.current = null;
    audioCtxRef.current = null; analyserRef.current = null;
    rafRef.current = null; silenceRef.current = null;
    sessionKeyRef.current = null; mimeTypeRef.current = null;
    elapsedRef.current = 0; startedAtRef.current = 0;
    seqRef.current = 0; zeroByteRef.current = 0;
    setSessionKey(null); setMimeType(null); setTimer(0); setLevel(0);
    syncState('idle');

    onCancelled?.();
    return true;
  }, [syncState, onCancelled]);

  // ── Unmount cleanup (belt-and-braces — T-30-10) ──
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      try { audioCtxRef.current?.close(); } catch {}
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch {}
    };
  }, []);

  return { start, pause, resume, stop, cancel, state, timer, level, sessionKey, mimeType };
}
